import { Share2, ArrowRightLeft } from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { Badge } from '@/components/ui/badge';
import { ImportanceBadge } from '@/components/ui/importance-badge';

interface IntertextualArc {
    source: string;
    target: string;
    note?: string;
    type?: 'Fulfillment' | 'Echo' | 'Typology' | 'Contrast' | 'Development';
    importance?: 'high' | 'medium' | 'low';
}

interface IntertextualArcsCardProps {
    arcs: IntertextualArc[];
    isExpanded: boolean;
    onToggleExpansion: () => void;
    onRefClick?: (ref: string) => void;
    thumbnailUrl?: string;
}

export function IntertextualArcsCard({ arcs, isExpanded, onToggleExpansion, onRefClick, thumbnailUrl }: IntertextualArcsCardProps) {
    if (!arcs || arcs.length === 0) return null;

    return (
        <DiscoveryCard
            isExpanded={isExpanded}
            onToggle={onToggleExpansion}
            icon={Share2}
            thumbnailUrl={thumbnailUrl}
            header={
                <div className="flex flex-col gap-1">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/50">Global Logic</div>
                    <h3 className="text-xl font-serif font-medium text-foreground/90 leading-tight">Intertextual Arcs & Links</h3>
                </div>
            }
        >
            <div className="pt-4 space-y-4">
                <p className="text-xs text-muted-foreground italic mb-2">
                    These arcs connect this book effectively to other parts of the biblical canon.
                </p>

                <div className="grid gap-3">
                    {arcs.map((arc, idx) => (
                        <div key={idx} className="flex flex-col p-4 rounded-xl border border-border/20 bg-background/30 hover:bg-background/50 transition-colors">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-y-2 gap-x-4 mb-3">
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div
                                        className="text-sm font-bold font-serif cursor-pointer hover:text-primary transition-colors whitespace-nowrap"
                                        onClick={() => onRefClick?.(arc.source)}
                                    >
                                        {arc.source}
                                    </div>
                                    <ArrowRightLeft className="h-3 w-3 text-muted-foreground/30 shrink-0" />
                                    <div
                                        className="text-sm font-bold font-serif cursor-pointer hover:text-primary transition-colors whitespace-nowrap"
                                        onClick={() => onRefClick?.(arc.target)}
                                    >
                                        {arc.target}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 self-start sm:self-auto">
                                    {arc.importance && <ImportanceBadge importance={arc.importance} />}
                                    {arc.type && (
                                        <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-widest px-2 py-0 h-5 bg-primary/5 border-primary/10 text-primary/60 whitespace-nowrap">
                                            {arc.type}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                            {arc.note && (
                                <p className="text-xs text-foreground/70 leading-relaxed font-light pl-4 border-l border-primary/10">
                                    {arc.note}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </DiscoveryCard>
    );
}
